// Server-authoritative checkout client. All order pricing, voucher validation and order
// creation now happen in petzify-backend (https://api.petzify.in). The web app never computes
// or sends money values — it sends the cart (product ids + quantities) and the backend prices
// everything from the products collection.
import { loadRazorpayScript } from '../utils/paymentService';
import { auth } from '../firebase/config';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.petzify.in';
const TOKEN_KEY = 'authToken';

export const setAuthToken = (token) => {
  if (token) localStorage.setItem(TOKEN_KEY, token);
};
export const getAuthToken = () => localStorage.getItem(TOKEN_KEY);
export const clearAuthToken = () => localStorage.removeItem(TOKEN_KEY);

// Email/password and OTP users get a backend-issued JWT (stored above). Google users are
// Firebase-authenticated, so we fall back to their Firebase ID token (the backend accepts both).
async function resolveToken() {
  const stored = getAuthToken();
  if (stored) return stored;
  try {
    if (auth?.currentUser) return await auth.currentUser.getIdToken();
  } catch (_) {
    // ignore — will send no token and get a 401
  }
  return null;
}

// Exchanges email/password for a backend session token (best-effort). Called after a
// successful client-side login/registration so checkout requests are authenticated.
export const fetchBackendToken = async (email, password) => {
  try {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (res.ok && data.token) {
      setAuthToken(data.token);
      return data.token;
    }
  } catch (_) {
    // ignore — Google users fall back to Firebase ID token; others retry on next login
  }
  return null;
};

async function apiFetch(path, { method = 'GET', body } = {}) {
  const token = await resolveToken();
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    // non-JSON response
  }

  if (res.status === 401) {
    throw new Error('Your session has expired. Please log in again.');
  }
  if (!res.ok) {
    throw new Error((data && data.error) || 'Request failed. Please try again.');
  }
  return data;
}

/**
 * Create an order on the backend. The backend prices items, validates the voucher, computes
 * the total, writes the order, and (for online) creates the matching Razorpay order.
 * @param {{ items: Array<{productId, quantity, variantId?}>, addressId: string,
 *           voucherCode?: string, paymentMethod: 'online'|'cod' }} payload
 */
export const createOrder = (payload) =>
  apiFetch('/api/checkout/create-order', { method: 'POST', body: { ...payload, platform: 'web' } });

export const verifyPayment = (payload) =>
  apiFetch('/api/payments/verify', { method: 'POST', body: payload });

export const fetchMyOrders = () => apiFetch('/api/orders');
export const fetchOrder = (id) => apiFetch(`/api/orders/${id}`);

/**
 * Opens the Razorpay checkout for an online order created by `createOrder`, using the
 * server-returned key/order id/amount (never a client amount). On success it verifies the
 * payment with the backend. The order is finalized by the Razorpay webhook.
 * @param {object} order  result of createOrder (online)
 * @param {object} user   { name, email, phone }
 */
export const payWithRazorpay = (order, user) =>
  new Promise(async (resolve, reject) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      reject(new Error('Unable to load payment gateway. Please check your connection.'));
      return;
    }

    const rzp = new window.Razorpay({
      key: order.key,
      amount: order.amount, // paise, from the backend / Razorpay order
      currency: order.currency || 'INR',
      name: 'Petzify',
      description: 'Order Payment',
      order_id: order.razorpayOrderId,
      prefill: {
        name: user?.name || '',
        email: user?.email || '',
        contact: user?.phone || '',
      },
      theme: { color: '#009688' },
      handler: async function (response) {
        try {
          const result = await verifyPayment({
            orderId: order.orderId,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          if (result && result.verified) {
            resolve({ success: true, orderId: order.orderId, paymentId: response.razorpay_payment_id });
          } else {
            reject(new Error((result && result.error) || 'Payment verification failed.'));
          }
        } catch (err) {
          reject(err);
        }
      },
      modal: {
        escape: false,
        ondismiss: function () {
          reject(new Error('Payment cancelled by user'));
        },
      },
    });

    rzp.on('payment.failed', function (response) {
      const message = response?.error?.description || response?.error?.reason || 'Payment failed';
      reject(new Error(message));
    });

    rzp.open();
  });
